/*
  Custom Autoship control for cart line items (drawer + cart page). Replaces
  Appstle's own injected cart widget (which was path-gated to /cart and couldn't be
  styled to match the drawer). The markup is in snippets/cart-items.liquid; this
  script turns the One-time / Autoship radios + frequency <select> into a
  /cart/change call that sets the line's `selling_plan` (an Appstle native selling
  plan), then re-renders the cart using the theme's OWN <cart-items> pipeline
  (getSectionsToRender + getElementHTML) so line prices, totals and the widget's own
  state all refresh exactly like a quantity change does.
*/
(function () {
  /* Writes a money string with Amazon-style cents, matching the Liquid-rendered prices around it.
     Falls back to plain text if llm-money.js is absent, so a missing script means flat cents rather
     than tags rendered as visible characters. */
  function setMoneyHtml(el, s) {
    if (window.llmMoney) el.innerHTML = window.llmMoney.cents(s || '');
    else el.textContent = s || '';
  }

  // The selling_plan id to apply for a widget's current UI state (null = one-time).
  function planFor(widget) {
    var autoship = widget.querySelector('input[value="autoship"]');
    if (!autoship || !autoship.checked) return null;
    var sel = widget.querySelector('.llm-autoship__freq');
    return sel && sel.value ? sel.value : null;
  }

  var busy = false;

  async function applyChange(widget) {
    var cartItems = document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return;
    if (busy) return;
    busy = true;
    widget.classList.add('llm-autoship--loading');

    var line = Number(widget.dataset.line);
    var plan = planFor(widget); // string id or null

    var toRender = cartItems.getSectionsToRender();
    var sections = [];
    toRender.forEach(function (s) {
      if (sections.indexOf(s.section) === -1) sections.push(s.section);
    });

    var url = (window.theme && theme.routes && theme.routes.cartChange) || '/cart/change.js';

    try {
      var res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: line,
          selling_plan: plan,
          sections: sections,
          sections_url: window.location.pathname
        })
      });
      var data = await res.json();
      if (!res.ok) throw new Error((data && data.message) || 'cart change failed');

      // Re-render the same sections the theme would after a quantity change.
      toRender.forEach(function (s) {
        var sectionEl = document.getElementById(s.id);
        if (!sectionEl) return;
        var el = sectionEl.querySelector(s.selector) || sectionEl;
        var html = data.sections && data.sections[s.section];
        if (html != null) el.innerHTML = cartItems.constructor.getElementHTML(html, s.selector);
      });

      // Let any cart listeners (counts/upsells) know the cart changed.
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    } catch (e) {
      console.error('[llm-autoship]', e);
      widget.classList.remove('llm-autoship--loading');
    } finally {
      busy = false;
    }
  }

  /* ---- Quick-view drawer: reflect the chosen Autoship price in the drawer -------
     There's no cart line yet, so instead of /cart/change we just repaint the theme's
     own price element. Data comes from the <script class="llm-qa-plandata"> map that
     Liquid embeds in the widget: { variantId: { onetime, <planId>: <price>, ... } }. */
  function qaData(widget) {
    var el = widget.querySelector('.llm-qa-plandata');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function qaVariantId() {
    var form = document.getElementById('product-form-quickadd');
    var input = form && form.querySelector('[name="id"]');
    return input ? input.value : null;
  }
  function renderQaPrice(widget) {
    var data = qaData(widget);
    if (!data) return;
    var vmap = data[qaVariantId()];
    if (!vmap) return;
    var drawer =
      widget.closest('quick-add-drawer') ||
      widget.closest('[class*="quick-add-drawer"]') ||
      document;
    var autoship = widget.querySelector('input[value="autoship"]');
    var freq = widget.querySelector('.llm-autoship__freq');
    var on = !!(autoship && autoship.checked);
    var sub = on && freq && freq.value ? vmap[freq.value] : null;

    drawer.querySelectorAll('.price').forEach(function (p) {
      var cur = p.querySelector('.price__current .js-value');
      if (!cur) return;
      var wasWrap = p.querySelector('.price__was');
      var wasVal = wasWrap && wasWrap.querySelector('.js-value');
      var wasLabel = wasWrap && wasWrap.querySelector('.js-label');
      if (on && sub) {
        // Stash the untouched (variant-picker-set) values once, so One-time restores cleanly.
        /* innerHTML, not textContent, on both the stash and the write (owner 2026-09-04). The
           price Liquid rendered now contains a <sup> around the cents; reading textContent threw
           that markup away, so switching to Autoship and back left a flat price sitting next to
           superscripted ones. The plan prices in llm-qa-plandata are still flat strings — they go
           through | json, where markup has no business — so they get the cents treatment here at
           render time instead. */
        if (!p.classList.contains('llm-qa-priced')) {
          cur.dataset.llmOrig = cur.innerHTML;
          if (wasVal) wasVal.dataset.llmOrig = wasVal.innerHTML;
          if (wasLabel) wasLabel.dataset.llmOrig = wasLabel.textContent;
          p.dataset.llmOrigSale = p.classList.contains('price--on-sale') ? '1' : '0';
        }
        setMoneyHtml(cur, sub);
        p.classList.add('price--on-sale', 'llm-qa-priced');
        if (wasVal) setMoneyHtml(wasVal, vmap.onetime);
        if (wasLabel) wasLabel.textContent = 'One-time price';
      } else if (p.classList.contains('llm-qa-priced')) {
        if (cur.dataset.llmOrig != null) cur.innerHTML = cur.dataset.llmOrig;
        if (wasVal) wasVal.innerHTML = wasVal.dataset.llmOrig || '';
        if (wasLabel) wasLabel.textContent = wasLabel.dataset.llmOrig || '';
        if (p.dataset.llmOrigSale === '0') p.classList.remove('price--on-sale');
        p.classList.remove('llm-qa-priced');
      }
    });
  }

  /* ---- PDP buy box: keep both option-card prices honest ------------------------
     The PDP picker (scope 'pdp') prints BOTH prices at once — one-time in its card, Autoship in
     its card — so unlike the drawer there is nothing to swap on selection: the comparison is the
     point. Only a VARIANT change invalidates them.

     This is why there is no main-price driver here. The audit flagged "the displayed price never
     changes" as a gap on the assumption we would repaint .product-info__price when Autoship is
     picked; showing both prices in the cards answers it without a second writer fighting
     variant-picker.js over the same node.

     Replaces the per-card price rendering Appstle did itself, and with it llm-money-appstle.js —
     which existed only because that price was app-rendered and out of reach of the theme's
     formatter. setMoneyHtml gives us the superscript cents natively. */
  function pdpVariantId(widget) {
    // Via the frequency select's form= association, so this never hard-codes a section id.
    var freq = widget.querySelector('.llm-autoship__freq');
    var formId = freq && freq.getAttribute('form');
    var form = formId && document.getElementById(formId);
    var input = form && form.querySelector('[name="id"]');
    if (input) return input.value;
    var fallback = document.querySelector('.llm-buybox-sidebar [name="id"]');
    return fallback ? fallback.value : null;
  }
  function renderPdpPrices(widget) {
    var el = widget.querySelector('.llm-pdp-plandata');
    if (!el) return;
    var data;
    try { data = JSON.parse(el.textContent); } catch (e) { return; }
    var vmap = data && data[pdpVariantId(widget)];
    if (!vmap) return;

    var one = widget.querySelector('[data-llm-price="onetime"]');
    if (one && vmap.onetime) setMoneyHtml(one, vmap.onetime);

    var freq = widget.querySelector('.llm-autoship__freq');
    var planPrice = freq && freq.value ? vmap[freq.value] : null;
    if (!planPrice) {
      // No frequency chosen yet (the select is disabled until Autoship is picked) — fall back to
      // any plan key, since every frequency carries the same percentage.
      for (var k in vmap) { if (k !== 'onetime') { planPrice = vmap[k]; break; } }
    }
    var auto = widget.querySelector('[data-llm-price="autoship"]');
    if (auto && planPrice) setMoneyHtml(auto, planPrice);
  }

  /* ---- PDP: the WHOLE card is a click target -----------------------------------
     Only the <label> head was clickable, so clicking the price, the benefits or the empty space did
     nothing (owner, 2026-10-12). The body is deliberately NOT inside the label — if it were, opening
     the frequency <select> would toggle the radio — so the rest of the card is wired up here instead.

     ⛔ IGNORE CLICKS ON ANYTHING INTERACTIVE. The card contains a real <select>, and the delivery
     line carries an (i) that opens a ZIP popup (llm-shipping-eta.js) plus links. Selecting the
     option out from under someone who was reaching for one of those is worse than the dead space
     this fixes. The label itself is excluded too: it already works natively, and handling it here as
     well would fire change twice. */
  var INTERACTIVE = 'a,button,select,input,textarea,label,summary,[role="button"],.info-ship,.popup-box-message';

  document.addEventListener('click', function (e) {
    var card = e.target.closest && e.target.closest('.llm-autoship--pdp .llm-autoship__card');
    if (!card) return;
    if (e.target.closest(INTERACTIVE)) return;

    var radio = card.querySelector('input[type="radio"]');
    if (!radio || radio.checked) return;        // already selected — nothing to do, no event to fire
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Per-option selected class. The active-card CSS gate
     (llm-product-highlights-v2.css:577-588) hides stock / delivery / footnote in the card that is
     NOT chosen — without this they show in both cards at once. Appstle supplied this via
     .appstle-active-option, which llm-autoship-toggle-v2.js:286-304 had to keep un-stale because
     Appstle left the class on deselected cards. Driven off :checked here, so it cannot go stale. */
  /* ⚠️ MUST stay value-scoped, not a bare input[type="radio"]. Appstle's hidden PDP widget injects
     its own `selling_plan` / `selling_plan_radio` inputs INSIDE our one-time card — one of them is
     always :checked, so a bare radio selector reads Appstle's state instead of ours and reports the
     wrong card as selected. Appstle's values are plan IDs or "", so matching on our two values is
     unambiguous. Measured on the preview 2026-10-12. */
  var OPTION_RADIOS = 'input[type="radio"][value="onetime"], input[type="radio"][value="autoship"]';

  /* ⚠️ Spelled out rather than OPTION_RADIOS + ':checked'. A pseudo-class appended to a
     comma-separated selector list binds to the LAST clause only, so the concatenated form matches
     any one-time radio whether checked or not — it silently always reported one-time as selected. */
  var OPTION_RADIO_CHECKED =
    'input[type="radio"][value="onetime"]:checked, input[type="radio"][value="autoship"]:checked';

  function syncCardState(widget) {
    widget.querySelectorAll('.llm-autoship__card').forEach(function (card) {
      var radio = card.querySelector(OPTION_RADIOS);
      card.classList.toggle('is-on', !!(radio && radio.checked));
    });
  }

  // Re-apply prices after a variant change (the picker replaces the price node).
  document.addEventListener('on:variant:change', function () {
    var qa = document.querySelector('.llm-autoship--qa');
    if (qa) setTimeout(function () { renderQaPrice(qa); }, 0);
    var pdp = document.querySelector('.llm-autoship--pdp');
    if (pdp) setTimeout(function () { renderPdpPrices(pdp); }, 0);
  });

  // First paint: Liquid already rendered the selected variant's prices, so this only matters when
  // something changed the variant before this script ran.
  document.addEventListener('DOMContentLoaded', function () {
    var pdp = document.querySelector('.llm-autoship--pdp');
    if (pdp) { renderPdpPrices(pdp); syncCardState(pdp); }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var widget = t.closest('.llm-autoship');
    if (!widget) return;
    var isRadio = t.matches('input[type="radio"]');
    var isFreq = t.classList && t.classList.contains('llm-autoship__freq');
    if (!isRadio && !isFreq) return;

    // Immediate UI feedback: enable/disable the frequency select + toggle class.
    var autoship = widget.querySelector('input[value="autoship"]');
    var freq = widget.querySelector('.llm-autoship__freq');
    var on = !!(autoship && autoship.checked);
    if (freq) freq.disabled = !on;
    widget.classList.toggle('is-autoship', on);
    // (The "dropdown missing on product-page quick-buy" bug was Appstle's CSS positioning our
    // name="selling_plan" select absolutely — fixed in llm-autoship.css, not here.)

    // Quick-view drawer: pre-purchase selector. The <select name="selling_plan"
    // form="product-form-quickadd"> is submitted with add-to-cart only while enabled,
    // so toggling `disabled` above is all that's needed — no cart change to apply.
    // Also repaint the drawer's price to reflect the chosen option.
    if (widget.classList.contains('llm-autoship--qa')) {
      renderQaPrice(widget);
      return;
    }

    /* PDP buy box. The <select name="selling_plan" form="product-form-<section>"> is submitted with
       add-to-cart only while enabled, so the `disabled` toggle above is the whole one-time path —
       nothing else to apply. Card prices do not depend on the selection (both are shown), but the
       chosen FREQUENCY can change which plan price is displayed, and the card state drives the
       active-card CSS gate. */
    if (widget.classList.contains('llm-autoship--pdp')) {
      syncCardState(widget);
      renderPdpPrices(widget);
      return;
    }

    // Cart line: only act on a real line item (avoids a bogus /cart/change).
    if (!widget.dataset.line) return;
    applyChange(widget);
  });

  /* ── LAYOUT EXPERIMENT — DELETE ONCE A LAYOUT IS CHOSEN ──────────────────────────────────────
     Four arrangements of the PDP buy box, so they can be compared on real products without a theme
     push per option. Append to any product URL:

       ?bb=amazon     one-time first AND selected, Add to cart inside it (Amazon's layout)
       ?bb=carewell   Autoship first but collapsed, one-time selected  (Carewell's layout)
       ?bb=chewy      Autoship first AND selected                      (Chewy's layout)
       ?bb=off        clear and go back to what's live today

     ENTIRELY OPT-IN. With no ?bb= and nothing stored, this function returns before touching
     anything and the buy box renders exactly as it does now — so it is harmless if it reaches live.
     The choice is remembered for the tab (sessionStorage) so several products can be walked in one
     layout without re-typing the parameter.

     Add to cart is MOVED, never copied: variant-picker.js rewrites its label, ship-with-autoship.js
     relabels it "Add to Autoship", and the sticky mobile bar forwards taps to `button[name="add"]`.
     A duplicate button would silently miss all three. The picker and .product-info__add-to-cart are
     siblings inside the same <form>, so this reparents within the form and the submit still works. */
  (function layoutExperiment() {
    var KEY = 'llm-bb-layout';
    var MODES = ['amazon', 'carewell', 'chewy'];

    var asked = new URLSearchParams(location.search).get('bb');
    if (asked === 'off') { try { sessionStorage.removeItem(KEY); } catch (e) {} return; }
    if (asked && MODES.indexOf(asked) >= 0) { try { sessionStorage.setItem(KEY, asked); } catch (e) {} }

    var mode = asked && MODES.indexOf(asked) >= 0 ? asked : null;
    if (!mode) { try { mode = sessionStorage.getItem(KEY); } catch (e) {} }
    if (!mode || MODES.indexOf(mode) < 0) return;

    function start() {
      var widget = document.querySelector('.llm-autoship--pdp');
      var atcBlock = document.querySelector('.product-info__add-to-cart');
      if (!widget || !atcBlock) return;

      widget.dataset.bb = mode;

      /* Card ORDER is CSS (llm-autoship.css, same data-bb attribute). Only the default SELECTION and
         the button's home are decided here. */
      var wantAutoship = mode === 'chewy';
      var radio = widget.querySelector('input[value="' + (wantAutoship ? 'autoship' : 'onetime') + '"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* Finds the selected card from the CHECKED RADIO, not from .is-on. syncCardState() applies
         is-on from its own DOMContentLoaded handler, and this script is deferred — so by the time
         start() runs, readyState is often already past 'loading' and no card carries is-on yet.
         Keying off :checked has no such ordering dependency. */
      function place() {
        var on = widget.querySelector(OPTION_RADIO_CHECKED);
        var card = on && on.closest('.llm-autoship__card');
        var body = card && card.querySelector('.llm-autoship__card-body');
        if (body && atcBlock.parentElement !== body) body.appendChild(atcBlock);
      }
      place();

      /* syncCardState runs on the same `change` event; defer so is-on is already correct. */
      widget.addEventListener('change', function () { setTimeout(place, 0); });
      document.addEventListener('on:variant:change', function () { setTimeout(place, 50); });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  })();
})();
